import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { replayTour } from "@/components/FirstTour";
import { NumbersBody } from "@/components/DayBoard";

function MyNumbers() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/me/day").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return null;
  return <div className="rounded-lg border border-slate-100 p-3" data-testid="profile-numbers"><div className="text-sm font-semibold text-slate-700 mb-2">Os meus números</div><NumbersBody d={d} /></div>;
}

function shrink(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const s = Math.min(img.width, img.height);
      const c = document.createElement("canvas");
      c.width = c.height = 192;
      c.getContext("2d").drawImage(img, (img.width - s) / 2, (img.height - s) / 2, s, s, 0, 0, 192, 192);
      URL.revokeObjectURL(img.src);
      resolve(c.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

function PhotoPick({ value, onChange }) {
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try { onChange(await shrink(file)); } catch { toast.error("Não consegui ler a imagem."); }
  };
  return (
    <div className="flex items-center gap-4" data-testid="profile-photo">
      {value
        ? <img src={value} alt="Fotografia" className="w-16 h-16 rounded-full object-cover border border-slate-200" data-testid="profile-photo-preview" />
        : <div className="w-16 h-16 rounded-full bg-slate-100 border border-dashed border-slate-300" />}
      <div className="space-y-1">
        <label className="inline-block text-sm font-medium text-ec-green hover:underline cursor-pointer">
          {value ? "Mudar fotografia" : "Adicionar fotografia"}
          <input type="file" accept="image/*" hidden onChange={pick} data-testid="profile-photo-input" />
        </label>
        {value && <button type="button" onClick={() => onChange("")} className="block text-xs text-slate-500 hover:underline" data-testid="profile-photo-remove">Remover</button>}
        <p className="text-xs text-slate-400">Aparece na página do cliente e no menu.</p>
      </div>
    </div>
  );
}

const split = (n) => { const w = (n || "").trim().split(/\s+/); return { first: w[0] || "", last: w.slice(1).join(" ") }; };

function Digest({ on, onChange }) {
  const test = async () => {
    try {
      const { data } = await api.post("/me/digest/test");
      toast[data.sent ? "success" : data.reason === "falhou" ? "error" : "info"](data.sent ? "Enviámos o resumo de hoje para o seu email"
        : data.reason === "falhou" ? "Não foi possível enviar o email. Tente mais tarde." : "Hoje não há nada para tratar: nesses dias não enviamos.");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <div className="rounded-xl border border-slate-200 p-4" data-testid="profile-digest">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-ec-green">Resumo diário por email</div>
          <p className="text-xs text-slate-500 mt-0.5">Dias úteis às 8h30: até 5 coisas para tratar, por urgência. Se não houver nada, não enviamos.</p>
        </div>
        <Switch checked={on} onCheckedChange={onChange} data-testid="profile-digest-switch" />
      </div>
      {on && <button type="button" onClick={test} className="mt-2 text-xs font-semibold text-ec-green hover:underline" data-testid="profile-digest-test">Enviar-me o resumo de hoje agora</button>}
    </div>
  );
}

export function ProfileDialog({ open, onOpenChange }) {
  const { setUser, user } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState(null);
  useEffect(() => {
    if (!open) return;
    api.get("/auth/me").then(({ data }) => setF({ ...split(data.name), phone: data.phone || "", nif: data.nif || "", cc_number: data.cc_number || "",
      kw_id: data.kw_id || "", photo: data.photo || "", daily_digest: data.daily_digest !== false }));
  }, [open]);
  const save = async () => {
    const { first, last, ...rest } = f;
    try {
      await api.put("/me/profile", { ...rest, name: `${first.trim()} ${last.trim()}`.trim() });
      toast.success("Perfil guardado");
      const { data } = await api.get("/auth/me");
      setUser(data);
      onOpenChange(false);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const field = (k, label, ph) => (
    <div><Label>{label}</Label><Input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={ph} className="mt-1.5" data-testid={`profile-${k}`} /></div>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="profile-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-ec-green">O meu perfil</DialogTitle>
          <DialogDescription>Usado no contrato de mediação e na página do cliente.</DialogDescription>
        </DialogHeader>
        {f && (
          <div className="space-y-4">
            <PhotoPick value={f.photo} onChange={(photo) => setF({ ...f, photo })} />
            {user?.role === "consultor" && <MyNumbers />}
            <div className="grid grid-cols-2 gap-3">
              {field("first", "Nome", "André")}
              {field("last", "Apelido", "Tavares")}
            </div>
            {!f.last.trim() && <p className="text-xs text-amber-700 -mt-2" data-testid="profile-name-incomplete">Falta o apelido: os clientes veem só «{f.first || "—"}».</p>}
            <div className="grid grid-cols-2 gap-3">
              {field("phone", "Telemóvel / WhatsApp", "912 345 678")}
              {field("kw_id", "KW ID", "")}
              {field("nif", "NIF", "9 dígitos")}
              {field("cc_number", "N.º do cartão de cidadão", "")}
            </div>
            <Digest on={f.daily_digest} onChange={(daily_digest) => setF({ ...f, daily_digest })} />
            <button onClick={save} disabled={!f.first.trim() || !f.last.trim()} className="w-full bg-ec-green text-white px-4 py-2.5 rounded-lg font-semibold hover:bg-ec-greenDark transition-colors disabled:opacity-50" data-testid="profile-save">Guardar</button>
            {user?.role === "consultor" && <button type="button" onClick={() => { onOpenChange(false); navigate("/dia"); setTimeout(() => replayTour(user.id), 300); }}
              className="w-full mt-2 border border-slate-300 bg-white text-slate-800 px-4 py-2.5 rounded-lg font-semibold hover:bg-slate-50" data-testid="profile-replay-intro">Rever introdução</button>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
